"use client";

import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import { Check, Copy, MicOff, RefreshCw, X } from "lucide-react";
import { useCallStore } from "../model/call-store";
import { MIC_ISSUE_MESSAGE, probeMicAccess, type MicIssue } from "../model/mic-access";

/**
 * MicIssueNotice — a PERSISTENT, actionable notice for a microphone problem.
 *
 * Why it isn't just the call's `errorMessage`: that copy lives inside the call
 * panel, and the panel is gone seconds later when the call tears down. A blocked
 * mic is an instruction, not a status — the fix happens in browser settings,
 * which takes longer than the call's own teardown and needs the steps to still be
 * on screen when the user comes back.
 *
 * No web API can open site settings — Chrome blocks page-initiated navigation to
 * `chrome://settings`, and there is no permission-revocation API. So the honest
 * affordance is: the exact steps, a copyable settings URL for browsers that have
 * one, and a re-check button for when the user has actually changed it.
 */

interface BrowserGuide {
  readonly steps: ReadonlyArray<string>;
  /** Pasteable settings URL — omitted where the browser has no such URL. */
  readonly settingsUrl: string | null;
}

/**
 * Chrome and Edge expose a pasteable settings URL; Safari's mic controls live in
 * the native Settings app, and Firefox clears its block from the address bar.
 * Order matters: Edge and Brave both contain "Chrome" in their UA.
 */
const guideFor = (userAgent: string): BrowserGuide => {
  if (/Edg\//.test(userAgent)) {
    return {
      steps: ["Click the lock icon at the left of the address bar", "Set Microphone to Allow", "Reload this page"],
      settingsUrl: "edge://settings/content/microphone"
    };
  }
  if (/Firefox\//.test(userAgent)) {
    return {
      steps: ["Click the blocked-microphone icon in the address bar", 'Clear the "Blocked Temporarily" permission', "Reload this page"],
      settingsUrl: null
    };
  }
  if (/Safari\//.test(userAgent) && !/Chrome\//.test(userAgent)) {
    return {
      steps: ["Safari menu → Settings → Websites → Microphone", "Set this site to Allow", "Reload this page"],
      settingsUrl: null
    };
  }
  return {
    steps: ["Click the sliders icon at the left of the address bar", "Set Microphone to Allow", "Reload this page"],
    settingsUrl: "chrome://settings/content/microphone"
  };
};
const TITLE: Record<MicIssue, string> = {
  blocked: "Microphone blocked",
  missing: "No microphone found",
  busy: "Microphone in use",
  unsupported: "Microphone unavailable here",
  unknown: "Microphone unavailable"
};
export function MicIssueNotice() {
  const $ = _c(52);
  const micIssue = useCallStore(_temp);
  const patch = useCallStore(_temp2);
  const [copied, setCopied] = useState(false);
  const [probe, setProbe] = useState("idle");
  const rechecking = probe === "checking";
  const [lastIssue, setLastIssue] = useState(micIssue);
  if (lastIssue !== micIssue) {
    setLastIssue(micIssue);
    setProbe("idle");
    setCopied(false);
  }
  if (micIssue === null) {
    return null;
  }
  let t0;
  if ($[0] !== patch) {
    t0 = () => patch({
      micIssue: null
    });
    $[0] = patch;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const dismiss = t0;
  let recheck;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[2] !== copied || $[3] !== dismiss || $[4] !== micIssue || $[5] !== patch) {
    const guide = micIssue === "blocked" ? guideFor(globalThis.navigator?.userAgent ?? "") : null;
    const copySettingsUrl = async () => {
      if (!guide?.settingsUrl) {
        return;
      }
      try {
        await globalThis.navigator.clipboard.writeText(guide.settingsUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch {}
    };
    let t7;
    if ($[13] !== patch) {
      t7 = async () => {
        setProbe("checking");
        const issue = await probeMicAccess();
        if (issue === null) {
          setProbe("ok");
          patch({
            micIssue: null
          });
          return;
        }
        patch({
          micIssue: issue
        });
        setProbe("blocked");
      };
      $[13] = patch;
      $[14] = t7;
    } else {
      t7 = $[14];
    }
    recheck = t7;
    t1 = "fixed bottom-4 right-4 z-130 flex w-80 max-w-[calc(100vw-2rem)] flex-col gap-3 rounded-2xl bg-[#0b1020] p-4 text-white shadow-2xl ring-1 ring-white/10";
    t2 = "alert";
    t3 = "mic-issue-title";
    t4 = "mic-issue-notice";
    let t8;
    if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-500/15 text-amber-300" aria-hidden={true}><MicOff className="h-4 w-4" /></span>;
      $[15] = t8;
    } else {
      t8 = $[15];
    }
    const t9 = TITLE[micIssue];
    let t10;
    if ($[16] !== t9) {
      t10 = <h2 id="mic-issue-title" className="text-sm font-semibold">{t9}</h2>;
      $[16] = t9;
      $[17] = t10;
    } else {
      t10 = $[17];
    }
    const t11 = micIssue === "blocked" ? "Calls need your microphone. Your browser is currently refusing it for this site." : MIC_ISSUE_MESSAGE[micIssue];
    let t12;
    if ($[18] !== t11) {
      t12 = <p className="mt-0.5 text-xs leading-relaxed text-white/60">{t11}</p>;
      $[18] = t11;
      $[19] = t12;
    } else {
      t12 = $[19];
    }
    let t13;
    if ($[20] !== t10 || $[21] !== t12) {
      t13 = <div className="min-w-0 flex-1">{t10}{t12}</div>;
      $[20] = t10;
      $[21] = t12;
      $[22] = t13;
    } else {
      t13 = $[22];
    }
    let t14;
    if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
      t14 = <X className="h-4 w-4" />;
      $[23] = t14;
    } else {
      t14 = $[23];
    }
    let t15;
    if ($[24] !== dismiss) {
      t15 = <button type="button" onClick={dismiss} aria-label="Dismiss" title="Dismiss" data-testid="mic-issue-dismiss" className="rounded-full p-1.5 text-white/70 transition-all active:scale-[0.97] hover:bg-white/10 hover:text-white focus:outline-hidden focus-visible:ring-2 focus-visible:ring-white/80">{t14}</button>;
      $[24] = dismiss;
      $[25] = t15;
    } else {
      t15 = $[25];
    }
    if ($[26] !== t13 || $[27] !== t15) {
      t5 = <header className="flex items-start gap-2.5">{t8}{t13}{t15}</header>;
      $[26] = t13;
      $[27] = t15;
      $[28] = t5;
    } else {
      t5 = $[28];
    }
    t6 = guide ? <><ol className="flex flex-col gap-1.5 text-xs text-white/75">{guide.steps.map(_temp3)}</ol>{guide.settingsUrl ? <button type="button" onClick={copySettingsUrl} data-testid="mic-issue-copy-url" className="flex items-center gap-2 rounded-lg bg-white/6 px-2.5 py-2 text-left text-[11px] transition-all active:scale-[0.99] hover:bg-white/10 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-white/80">{copied ? <Check className="h-3.5 w-3.5 shrink-0 text-emerald-400" aria-hidden={true} /> : <Copy className="h-3.5 w-3.5 shrink-0 text-white/60" aria-hidden={true} />}<code className="truncate font-mono text-white/80">{guide.settingsUrl}</code><span className="ml-auto shrink-0 text-white/50">{copied ? "Copied" : "Copy"}</span></button> : null}</> : null;
    $[2] = copied;
    $[3] = dismiss;
    $[4] = micIssue;
    $[5] = patch;
    $[6] = recheck;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
  } else {
    recheck = $[6];
    t1 = $[7];
    t2 = $[8];
    t3 = $[9];
    t4 = $[10];
    t5 = $[11];
    t6 = $[12];
  }
  let t7;
  if ($[29] !== probe) {
    t7 = probe === "blocked" ? <p className="text-[11px] leading-relaxed text-amber-300" role="status">Still blocked — the browser refused the microphone. Work through the steps above, then check again.</p> : null;
    $[29] = probe;
    $[30] = t7;
  } else {
    t7 = $[30];
  }
  let t8;
  if ($[31] !== recheck) {
    t8 = () => void recheck();
    $[31] = recheck;
    $[32] = t8;
  } else {
    t8 = $[32];
  }
  const t9 = `h-3.5 w-3.5 ${rechecking ? "animate-spin" : ""}`;
  let t10;
  if ($[33] !== t9) {
    t10 = <RefreshCw className={t9} aria-hidden={true} />;
    $[33] = t9;
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  const t11 = rechecking ? "Checking\u2026" : probe === "blocked" ? "Check again" : "I've allowed it";
  let t12;
  if ($[35] !== rechecking || $[36] !== t10 || $[37] !== t11 || $[38] !== t8) {
    t12 = <button type="button" onClick={t8} disabled={rechecking} data-testid="mic-issue-recheck" className="flex h-9 flex-1 items-center justify-center gap-1.5 rounded-full bg-[#5651ff] px-3 text-xs font-semibold text-white transition-all active:scale-[0.97] hover:bg-[#4a45e0] focus:outline-hidden focus-visible:ring-2 focus-visible:ring-white/80 disabled:opacity-60">{t10}{t11}</button>;
    $[35] = rechecking;
    $[36] = t10;
    $[37] = t11;
    $[38] = t8;
    $[39] = t12;
  } else {
    t12 = $[39];
  }
  let t13;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <button type="button" onClick={_temp4} data-testid="mic-issue-reload" className="flex h-9 items-center justify-center rounded-full bg-white/6 px-3 text-xs font-medium text-white/80 transition-all active:scale-[0.97] hover:bg-white/10 hover:text-white focus:outline-hidden focus-visible:ring-2 focus-visible:ring-white/80">Reload</button>;
    $[40] = t13;
  } else {
    t13 = $[40];
  }
  let t14;
  if ($[41] !== t12) {
    t14 = <div className="flex items-center gap-2">{t12}{t13}</div>;
    $[41] = t12;
    $[42] = t14;
  } else {
    t14 = $[42];
  }
  let t15;
  if ($[43] !== t1 || $[44] !== t14 || $[45] !== t2 || $[46] !== t3 || $[47] !== t4 || $[48] !== t5 || $[49] !== t6 || $[50] !== t7) {
    t15 = <section className={t1} role={t2} aria-labelledby={t3} data-testid={t4}>{t5}{t6}{t7}{t14}</section>;
    $[43] = t1;
    $[44] = t14;
    $[45] = t2;
    $[46] = t3;
    $[47] = t4;
    $[48] = t5;
    $[49] = t6;
    $[50] = t7;
    $[51] = t15;
  } else {
    t15 = $[51];
  }
  return t15;
}
function _temp4() {
  return globalThis.location.reload();
}
function _temp3(step, i) {
  return <li key={step} className="flex gap-2"><span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-white/10 text-[10px] font-semibold tabular-nums text-white/70" aria-hidden={true}>{i + 1}</span><span className="leading-relaxed">{step}</span></li>;
}
function _temp2(s_0) {
  return s_0.patch;
}
function _temp(s) {
  return s.micIssue;
}